import { invalidateAuthSession } from '../auth/session'
import type { ApiError } from '../types/api'
import { resolveOrgSlug, tokenKey } from '../utils/org-slug'

function getApiBase(): string {
  const slug = resolveOrgSlug()
  return slug ? `/api/${slug}` : '/api'
}

/**
 * Mint an end-to-end request id for this browser request. The id travels as
 * `X-Request-Id` through Caddy/nginx into Fastify (`genReqId`), which echoes
 * it back and stamps it on pino log lines + trace records — so one id joins
 * Caddy errors, app logs, and traces. crypto.randomUUID when available;
 * falls back to a counter+timestamp hex that still matches the backend's
 * `[A-Za-z0-9_-]{1,64}` shape.
 */
let requestIdCounter = 0

export function newRequestId(): string {
  try {
    const uuid = globalThis.crypto?.randomUUID?.()
    if (typeof uuid === 'string' && uuid.length > 0) return uuid
  } catch {
    // fall through to the local fallback below
  }
  requestIdCounter = (requestIdCounter + 1) % 0xffff
  const counter = requestIdCounter.toString(16).padStart(4, '0')
  const stamp = Date.now().toString(16)
  return `web-${stamp}-${counter}`
}

class ApiClient {
  private csrfToken: string | null = null
  private refreshPromise: Promise<string> | null = null

  /**
   * Set an MCP API key to authenticate with instead of the JWT session.
   * The backend's auth middleware accepts either: it verifies the JWT first
   * and falls back to validating a ch_-prefixed (or legacy 64-char hex)
   * MCP API key as a Bearer token (see `backend/api/middleware/auth.ts`).
   * Persisted to localStorage so it survives a reload, mirroring the JWT tokens.
   */
  setApiKey(apiKey: string | null): void {
    if (apiKey) {
      localStorage.setItem(tokenKey('apiKey'), apiKey)
    } else {
      localStorage.removeItem(tokenKey('apiKey'))
    }
  }

  getApiKey(): string | null {
    return localStorage.getItem(tokenKey('apiKey'))
  }

  private getAccessToken(): string | null {
    return localStorage.getItem(tokenKey('accessToken'))
  }

  /**
   * Bearer token to send: JWT access token takes priority when present
   * (refresh-on-401 only applies to it); an API key is the fallback for
   * callers authenticating as an MCP key rather than a logged-in user.
   */
  private getAuthToken(): string | null {
    return this.getAccessToken() ?? this.getApiKey()
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(tokenKey('refreshToken'))
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(tokenKey('accessToken'), accessToken)
    localStorage.setItem(tokenKey('refreshToken'), refreshToken)
  }

  private invalidateSession(): void {
    invalidateAuthSession()
  }

  /**
   * Attempt to refresh the access token using the refresh token.
   * Uses a promise lock to prevent concurrent refresh attempts.
   */
  private async attemptTokenRefresh(): Promise<string> {
    if (this.refreshPromise) return this.refreshPromise

    this.refreshPromise = (async () => {
      const refreshToken = this.getRefreshToken()
      if (!refreshToken) throw new Error('No refresh token available')

      const response = await fetch(`${getApiBase()}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include',
      })

      if (!response.ok) {
        this.invalidateSession()
        throw new Error('Token refresh failed')
      }

      const data = await response.json()
      // Store rotated refresh token if server returned one (rotation), otherwise keep current
      const newRefreshToken = (data.refreshToken as string) ?? refreshToken
      this.setTokens(data.accessToken, newRefreshToken)
      return data.accessToken as string
    })()

    try {
      return await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  /**
   * Fetch a fresh CSRF token from the server.
   * The server also sets a signed cookie that the backend validates.
   */
  private async fetchCsrfToken(): Promise<string> {
    const response = await fetch(`${getApiBase()}/csrf-token`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token')
    }
    const data = await response.json()
    this.csrfToken = data.csrfToken as string
    return this.csrfToken
  }

  /**
   * Get a valid CSRF token, fetching a new one if needed.
   */
  private async getCsrfToken(): Promise<string> {
    if (this.csrfToken) return this.csrfToken
    return this.fetchCsrfToken()
  }

  /**
   * Returns true for HTTP methods that change state and require CSRF protection.
   */
  private isStateChangingMethod(method: string): boolean {
    const m = method.toUpperCase()
    return m === 'POST' || m === 'PUT' || m === 'DELETE' || m === 'PATCH'
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const usingApiKey = !this.getAccessToken() && Boolean(this.getApiKey())
    const token = this.getAuthToken()
    const headers: HeadersInit = {
      'X-Request-Id': newRequestId(),
      ...options.headers,
    }

    // Only set JSON content-type when sending a body. Fastify rejects
    // Content-Type: application/json with an empty body (DELETE/GET) with 400
    // FST_ERR_CTP_EMPTY_JSON_BODY.
    if (options.body != null) {
      ;(headers as Record<string, string>)['Content-Type'] ??= 'application/json'
    }

    if (token) {
      ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
    }

    // Add CSRF token for state-changing requests. Skipped for API-key auth:
    // the server exempts any Bearer-authenticated request from CSRF (see
    // `shouldSkipCsrfForRequest` in backend/api/routes/csrf.ts) since
    // Authorization headers aren't auto-attached cross-site like cookies.
    const method = (options.method ?? 'GET').toUpperCase()
    if (this.isStateChangingMethod(method) && !usingApiKey) {
      const csrf = await this.getCsrfToken()
      ;(headers as Record<string, string>)['X-CSRF-Token'] = csrf
    }

    const response = await fetch(`${getApiBase()}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    })

    // If CSRF validation failed, retry once with a fresh token
    if (response.status === 403 && this.isStateChangingMethod(method)) {
      const body = await response
        .clone()
        .json()
        .catch(() => null)
      if (body?.message?.includes('CSRF') || body?.message?.includes('Missing CSRF')) {
        this.csrfToken = null
        const freshCsrf = await this.getCsrfToken()
        ;(headers as Record<string, string>)['X-CSRF-Token'] = freshCsrf
        const retryResponse = await fetch(`${getApiBase()}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include',
        })
        if (!retryResponse.ok) {
          let error: ApiError
          try {
            error = await retryResponse.json()
          } catch {
            error = {
              error: 'Network Error',
              message: `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
            }
          }
          throw new ApiClientError(error.message, retryResponse.status, error)
        }
        return this.parseBody(retryResponse)
      }
    }

    // If 401 and we have a refresh token, attempt to refresh and retry once
    if (response.status === 401 && !isRetry && this.getRefreshToken()) {
      try {
        const newAccessToken = await this.attemptTokenRefresh()
        ;(headers as Record<string, string>)['Authorization'] = `Bearer ${newAccessToken}`
        const retryResponse = await fetch(`${getApiBase()}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include',
        })
        if (!retryResponse.ok) {
          let error: ApiError
          try {
            error = await retryResponse.json()
          } catch {
            error = {
              error: 'Network Error',
              message: `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
            }
          }
          if (retryResponse.status === 401) {
            this.invalidateSession()
          }
          throw new ApiClientError(error.message, retryResponse.status, error)
        }
        return this.parseBody(retryResponse)
      } catch (err) {
        if (err instanceof ApiClientError) throw err
        this.invalidateSession()
        throw new ApiClientError('Session expired. Please log in again.', 401, {
          error: 'Unauthorized',
          message: 'Session expired',
        })
      }
    }

    if (!response.ok) {
      let error: ApiError
      try {
        error = await response.json()
      } catch {
        error = {
          error: 'Network Error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        }
      }
      if (response.status === 401) {
        this.invalidateSession()
      }
      throw new ApiClientError(error.message, response.status, error)
    }

    return this.parseBody(response)
  }

  /**
   * Reads a successful response body. Returns `undefined` for empty payloads
   * (e.g. 204 No Content or a 200 with no body) instead of failing on
   * `response.json()`, which throws `Unexpected end of JSON input` on an
   * empty body.
   */
  private async parseBody<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T
    const text = await response.text()
    if (!text) return undefined as T
    return JSON.parse(text) as T
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async openStream(endpoint: string, signal: AbortSignal): Promise<Response> {
    const requestId = newRequestId()
    const fetchStream = (token: string | null) =>
      fetch(`${getApiBase()}${endpoint}`, {
        method: 'GET',
        headers: {
          'X-Request-Id': requestId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        signal,
      })

    let response = await fetchStream(this.getAuthToken())
    if (response.status === 401 && this.getRefreshToken()) {
      try {
        response = await fetchStream(await this.attemptTokenRefresh())
      } catch (error) {
        if (signal.aborted) throw error
        this.invalidateSession()
        throw new ApiClientError('Session expired. Please log in again.', 401, {
          error: 'Unauthorized',
          message: 'Session expired',
        })
      }
    }
    if (!response.ok) {
      if (response.status === 401) this.invalidateSession()
      throw new ApiClientError(
        `Event stream failed with HTTP ${response.status}`,
        response.status,
        {
          error: 'Event Stream Error',
          message: `Event stream failed with HTTP ${response.status}`,
        }
      )
    }
    return response
  }

  /**
   * POST a JSON body and return the raw streaming `Response` (SSE / chunked).
   * Goes through the org-aware base URL, CSRF, and token-refresh handling —
   * callers must not hand-roll `fetch('/api/...')` for streaming endpoints or
   * the org slug is lost and multi-tenant deployments 404 (tenant resolver
   * treats the first path segment as an org slug).
   */
  async postStream(endpoint: string, data: unknown, signal?: AbortSignal): Promise<Response> {
    const usingApiKey = !this.getAccessToken() && Boolean(this.getApiKey())
    const body = JSON.stringify(data ?? {})

    const send = (token: string | null, csrf: string | null) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Request-Id': newRequestId(),
      }
      if (token) headers['Authorization'] = `Bearer ${token}`
      if (csrf) headers['X-CSRF-Token'] = csrf
      return fetch(`${getApiBase()}${endpoint}`, {
        method: 'POST',
        headers,
        body,
        credentials: 'include',
        ...(signal ? { signal } : {}),
      })
    }

    let response = await send(this.getAuthToken(), usingApiKey ? null : await this.getCsrfToken())

    // Retry once with a fresh CSRF token on CSRF rejection.
    if (response.status === 403 && !usingApiKey) {
      const parsed = await response
        .clone()
        .json()
        .catch(() => null)
      if (parsed?.message?.includes('CSRF') || parsed?.message?.includes('Missing CSRF')) {
        this.csrfToken = null
        response = await send(this.getAuthToken(), await this.getCsrfToken())
      }
    }

    // Retry once after refreshing an expired access token.
    if (response.status === 401 && this.getRefreshToken()) {
      try {
        const refreshed = await this.attemptTokenRefresh()
        response = await send(refreshed, usingApiKey ? null : await this.getCsrfToken())
      } catch (error) {
        if (signal?.aborted) throw error
        this.invalidateSession()
        throw new ApiClientError('Session expired. Please log in again.', 401, {
          error: 'Unauthorized',
          message: 'Session expired',
        })
      }
    }

    if (!response.ok) {
      if (response.status === 401) this.invalidateSession()
      let error: ApiError
      try {
        error = await response.json()
      } catch {
        error = { error: 'Stream Error', message: `HTTP ${response.status}` }
      }
      throw new ApiClientError(error.message, response.status, error)
    }

    return response
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    })
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data ?? {}),
    })
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data ?? {}),
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  async upload<T>(endpoint: string, file: File, fields?: Record<string, string>): Promise<T> {
    const usingApiKey = !this.getAccessToken() && Boolean(this.getApiKey())
    const token = this.getAuthToken()
    const formData = new FormData()
    // Non-file fields must be appended BEFORE the file part: @fastify/multipart
    // exposes fields collected up to the point the file stream is consumed.
    if (fields) {
      for (const [name, fieldValue] of Object.entries(fields)) {
        formData.append(name, fieldValue)
      }
    }
    formData.append('file', file)

    const headers: HeadersInit = { 'X-Request-Id': newRequestId() }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Add CSRF token for upload (POST); skipped for API-key auth — see request().
    if (!usingApiKey) {
      const csrf = await this.getCsrfToken()
      headers['X-CSRF-Token'] = csrf
    }

    const response = await fetch(`${getApiBase()}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    })

    // If CSRF validation failed, retry once with a fresh token
    if (response.status === 403) {
      const body = await response
        .clone()
        .json()
        .catch(() => null)
      if (body?.message?.includes('CSRF') || body?.message?.includes('Missing CSRF')) {
        this.csrfToken = null
        const freshCsrf = await this.getCsrfToken()
        headers['X-CSRF-Token'] = freshCsrf
        const retryResponse = await fetch(`${getApiBase()}${endpoint}`, {
          method: 'POST',
          headers,
          body: formData,
          credentials: 'include',
        })
        if (!retryResponse.ok) {
          let error: ApiError
          try {
            error = await retryResponse.json()
          } catch {
            const limitHint =
              retryResponse.status === 413
                ? 'File exceeds the upload size limit. Increase MAX_UPLOAD_SIZE_MB in the server .env file.'
                : `HTTP ${retryResponse.status}: ${retryResponse.statusText}`
            error = {
              error: retryResponse.status === 413 ? 'Payload Too Large' : 'Network Error',
              message: limitHint,
            }
          }
          throw new ApiClientError(error.message, retryResponse.status, error)
        }
        return this.parseBody(retryResponse)
      }
    }

    if (!response.ok) {
      let error: ApiError
      try {
        error = await response.json()
      } catch {
        const limitHint =
          response.status === 413
            ? 'File exceeds the upload size limit. Increase MAX_UPLOAD_SIZE_MB in the server .env file.'
            : `HTTP ${response.status}: ${response.statusText}`
        error = {
          error: response.status === 413 ? 'Payload Too Large' : 'Network Error',
          message: limitHint,
        }
      }
      if (response.status === 401) {
        this.invalidateSession()
      }
      throw new ApiClientError(error.message, response.status, error)
    }

    return this.parseBody(response)
  }

  async download(endpoint: string, filename: string): Promise<void> {
    const token = this.getAuthToken()
    const headers: HeadersInit = { 'X-Request-Id': newRequestId() }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${getApiBase()}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      let error: ApiError
      try {
        error = await response.json()
      } catch {
        error = {
          error: 'Network Error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        }
      }
      if (response.status === 401) {
        this.invalidateSession()
      }
      throw new ApiClientError(error.message, response.status, error)
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }
}

export class ApiClientError extends Error {
  status: number
  apiError: ApiError

  constructor(message: string, status: number, apiError: ApiError) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.apiError = apiError
  }
}

export const apiClient = new ApiClient()
